// Guitar tuning, string sets, and fret <-> pitch helpers.
//
// String indices run 0..5 from the low E to the high E:
//   0 = low E (E2), 1 = A (A2), 2 = D (D3), 3 = G (G3), 4 = B (B3), 5 = high E (E4)
// A voicing family resolves the user's lower/upper register choice to a
// concrete four-string layout. Drop 2 uses adjacent strings; Drop 3 skips the
// string immediately above its separated bass voice.

import type { PitchClass } from './notes';
import { sharpName } from './notes';
import type { VoicingFamily } from './voicingFamilies';

export type StringSet = 'middle' | 'upper';
export type StringLayoutId = 'adgb' | 'dgbe' | 'edgb' | 'agbe' | 'joe-pass';

export interface StringLayout {
  id: StringLayoutId;
  family: VoicingFamily;
  register: StringSet;
  strings: readonly number[];
}

/** MIDI pitch of each open string: E2 A2 D3 G3 B3 E4. */
export const OPEN_STRING_MIDI = [40, 45, 50, 55, 59, 64] as const;

/** Pitch class of each open string. */
export const OPEN_STRING_PC: PitchClass[] = OPEN_STRING_MIDI.map((m) => m % 12);

/** Human labels for each string, low to high. */
export const STRING_LABELS = ['E', 'A', 'D', 'G', 'B', 'e'] as const;

export const STRING_LAYOUTS: Record<StringLayoutId, StringLayout> = {
  adgb: {
    id: 'adgb',
    family: 'drop-2',
    register: 'middle',
    strings: [1, 2, 3, 4],
  },
  dgbe: {
    id: 'dgbe',
    family: 'drop-2',
    register: 'upper',
    strings: [2, 3, 4, 5],
  },
  edgb: {
    id: 'edgb',
    family: 'drop-3',
    register: 'middle',
    strings: [0, 2, 3, 4],
  },
  agbe: {
    id: 'agbe',
    family: 'drop-3',
    register: 'upper',
    strings: [1, 3, 4, 5],
  },
  'joe-pass': {
    id: 'joe-pass',
    family: 'joe-pass',
    register: 'middle',
    strings: [0, 1, 2, 3, 4, 5],
  },
};

const FAMILY_LAYOUTS: Record<VoicingFamily, Record<StringSet, StringLayoutId>> = {
  'drop-2': { middle: 'adgb', upper: 'dgbe' },
  'drop-3': { middle: 'edgb', upper: 'agbe' },
  'joe-pass': { middle: 'joe-pass', upper: 'joe-pass' },
};

export function stringLayoutFor(family: VoicingFamily, stringSet: StringSet): StringLayout {
  return STRING_LAYOUTS[FAMILY_LAYOUTS[family][stringSet]];
}

export function activeStrings(stringSet: StringSet, family: VoicingFamily = 'drop-2'): number[] {
  return [...stringLayoutFor(family, stringSet).strings];
}

export interface FingeringLayoutRef {
  stringSet: StringSet;
  stringLayout?: StringLayoutId;
  voicingFamily?: VoicingFamily;
  frets?: readonly (number | null)[];
}

/** Resolve the actual played strings from the fingering, never from UI state. */
export function activeStringsForFingering(fingering: FingeringLayoutRef): number[] {
  if (fingering.stringLayout === 'joe-pass' || fingering.voicingFamily === 'joe-pass') {
    return fingering.frets
      ? [0, 1, 2, 3, 4, 5].filter((s) => fingering.frets![s] != null)
      : [0, 1, 2, 3, 4, 5];
  }
  const layout = fingering.stringLayout
    ? STRING_LAYOUTS[fingering.stringLayout]
    : stringLayoutFor(fingering.voicingFamily ?? 'drop-2', fingering.stringSet);
  return [...layout.strings];
}

/** The string index carrying the top (melody) voice for a string set. */
export function topString(stringSet: StringSet, family: VoicingFamily = 'drop-2'): number {
  const strings = stringLayoutFor(family, stringSet).strings;
  return strings[strings.length - 1];
}

export function topStringForFingering(fingering: FingeringLayoutRef): number {
  const strings = activeStringsForFingering(fingering);
  if (fingering.stringLayout === 'joe-pass' && fingering.frets && strings.length) {
    return strings.reduce((top, string) =>
      frettedMidi(string, fingering.frets![string]!) > frettedMidi(top, fingering.frets![top]!)
        ? string
        : top,
    );
  }
  return strings[strings.length - 1];
}

/** Label for the string carrying the guide line / top voice. */
export function guideStringLabel(stringSet: StringSet, family: VoicingFamily = 'drop-2'): string {
  if (family === 'joe-pass') return 'highest sounding voice';
  return topString(stringSet, family) === 5 ? 'E string' : 'B string';
}

/** Pitch class sounding at a given string + fret. */
export function pitchClassAtFret(stringIndex: number, fret: number): PitchClass {
  return (OPEN_STRING_PC[stringIndex] + fret) % 12;
}

/** Sharp-spelled note name sounding at a given string + fret. */
export function noteAtFret(stringIndex: number, fret: number): string {
  return sharpName(pitchClassAtFret(stringIndex, fret));
}

/** Absolute MIDI pitch sounding at a given string + fret. */
export function frettedMidi(stringIndex: number, fret: number): number {
  return OPEN_STRING_MIDI[stringIndex] + fret;
}
